import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  tabPanel: {
    minHeight: 280,
  },
  mediaPreview: {
    maxWidth: 120,
    maxHeight: 120,
    objectFit: "cover",
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
}));

const TEXT = "text";
const MEDIA = "media";
const LOCATION = "location";

function parseMapsUrl(url) {
  if (!url || typeof url !== "string") return { latitude: null, longitude: null };
  const trimmed = url.trim();
  const atMatch = trimmed.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (atMatch) return { latitude: parseFloat(atMatch[1]), longitude: parseFloat(atMatch[2]) };
  const qMatch = trimmed.match(/[?&]q=(-?\d+\.?\d*)[,+ ](-?\d+\.?\d*)/);
  if (qMatch) return { latitude: parseFloat(qMatch[1]), longitude: parseFloat(qMatch[2]) };
  const queryMatch = trimmed.match(/[?&]query=(-?\d+\.?\d*)[,+ ](-?\d+\.?\d*)/);
  if (queryMatch) return { latitude: parseFloat(queryMatch[1]), longitude: parseFloat(queryMatch[2]) };
  const llMatch = trimmed.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (llMatch) return { latitude: parseFloat(llMatch[1]), longitude: parseFloat(llMatch[2]) };
  return { latitude: null, longitude: null };
}

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    type: TEXT,
    shortcut: "",
    message: "",
    title: "",
    mediaType: "image",
    mediaPath: "",
    caption: "",
    mapsUrl: "",
    latitude: null,
    longitude: null,
    locationName: "",
    locationCaption: "",
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);
  const [tabValue, setTabValue] = useState(0);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        const mapped = { ...initialState, ...initialValues };
        if (initialValues.latitude != null && initialValues.longitude != null) {
          mapped.mapsUrl = `https://www.google.com/maps?q=${initialValues.latitude},${initialValues.longitude}`;
        }
        setQuickAnswer(mapped);
        setTabValue(
          initialValues.type === MEDIA ? 1 : initialValues.type === LOCATION ? 2 : 0
        );
        return;
      }

      if (!quickAnswerId) {
        setQuickAnswer(initialState);
        setTabValue(0);
        return;
      }

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          const mapped = { ...data };
          if (data.latitude != null && data.longitude != null) {
            mapped.mapsUrl = `https://www.google.com/maps?q=${data.latitude},${data.longitude}`;
          }
          setQuickAnswer(mapped);
          setTabValue(
            data.type === MEDIA ? 1 : data.type === LOCATION ? 2 : 0
          );
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
    setTabValue(0);
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    const type = newValue === 0 ? TEXT : newValue === 1 ? MEDIA : LOCATION;
    setQuickAnswer((prev) => ({ ...prev, type }));
  };

  const handleMediaFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/quickAnswers/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (isMounted.current) {
        setQuickAnswer((prev) => ({
          ...prev,
          mediaPath: data.path,
          mediaType:
            file.type.startsWith("image/")
              ? "image"
              : file.type.startsWith("video/")
              ? "video"
              : "file",
        }));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleMapsUrlBlur = () => {
    const { latitude, longitude } = parseMapsUrl(quickAnswer.mapsUrl);
    setQuickAnswer((prev) => ({ ...prev, latitude, longitude }));
  };

  const handleSaveQuickAnswer = async (values) => {
    const type = values.type || (tabValue === 0 ? TEXT : tabValue === 1 ? MEDIA : LOCATION);
    let payload = { type };

    if (type === TEXT) {
      payload.shortcut = values.shortcut || "";
      payload.message = values.message || "";
      payload.title = values.shortcut;
    } else if (type === MEDIA) {
      payload.title = values.title || "";
      payload.mediaType = values.mediaType || "image";
      payload.mediaPath = values.mediaPath || null;
      payload.caption = values.caption || null;
    } else if (type === LOCATION) {
      const { latitude, longitude } = parseMapsUrl(values.mapsUrl);
      payload.title = values.title || values.locationName || "";
      payload.latitude = latitude;
      payload.longitude = longitude;
      payload.locationName = values.locationName || null;
      payload.locationCaption = values.locationCaption || null;
    }

    try {
      if (quickAnswerId) {
        await api.put(`/quickAnswers/${quickAnswerId}`, payload);
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", payload);
        if (onSave) onSave(data);
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const getValidationSchema = () => {
    if (tabValue === 0) {
      return Yup.object().shape({
        shortcut: Yup.string().min(2).max(15).required("Required"),
        message: Yup.string().min(8).max(30000).required("Required"),
      });
    }
    if (tabValue === 1) {
      return Yup.object().shape({
        title: Yup.string().required("Required"),
        mediaType: Yup.string().oneOf(["image", "video", "file"]).required(),
        mediaPath: Yup.string().required("Required"),
        caption: Yup.string().nullable(),
      });
    }
    return Yup.object().shape({
      title: Yup.string().required("Required"),
      mapsUrl: Yup.string()
        .required("Required")
        .test("valid-coords", "Invalid maps URL or coordinates", (val) => {
          const { latitude, longitude } = parseMapsUrl(val);
          return latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude);
        }),
      locationName: Yup.string().nullable(),
      locationCaption: Yup.string().nullable(),
    });
  };

  const mediaPreviewUrl =
    quickAnswer.mediaPath && quickAnswer.mediaType === "image"
      ? `${getBackendUrl()}public/${quickAnswer.mediaPath}`
      : null;

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {quickAnswerId
            ? i18n.t("quickAnswersModal.title.edit")
            : i18n.t("quickAnswersModal.title.add")}
        </DialogTitle>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={i18n.t("quickAnswersModal.tabs.text")} />
          <Tab label={i18n.t("quickAnswersModal.tabs.media")} />
          <Tab label={i18n.t("quickAnswersModal.tabs.location")} />
        </Tabs>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize
          validationSchema={getValidationSchema()}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveQuickAnswer(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.tabPanel}>
                  {tabValue === 0 && (
                    <>
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.shortcut")}
                          name="shortcut"
                          autoFocus
                          error={touched.shortcut && Boolean(errors.shortcut)}
                          helperText={touched.shortcut && errors.shortcut}
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          fullWidth
                        />
                      </div>
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.message")}
                          name="message"
                          error={touched.message && Boolean(errors.message)}
                          helperText={touched.message && errors.message}
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          multiline
                          rows={5}
                          fullWidth
                        />
                      </div>
                    </>
                  )}

                  {tabValue === 1 && (
                    <>
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.title")}
                          name="title"
                          error={touched.title && Boolean(errors.title)}
                          helperText={touched.title && errors.title}
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          fullWidth
                        />
                      </div>
                      <div className={classes.textQuickAnswerContainer}>
                        <FormControl variant="outlined" fullWidth size="small" margin="dense">
                          <InputLabel>{i18n.t("quickAnswersModal.form.mediaType")}</InputLabel>
                          <Select
                            name="mediaType"
                            value={values.mediaType || "image"}
                            onChange={(e) => setFieldValue("mediaType", e.target.value)}
                            label={i18n.t("quickAnswersModal.form.mediaType")}
                          >
                            <MenuItem value="image">{i18n.t("quickAnswersModal.mediaTypes.image")}</MenuItem>
                            <MenuItem value="video">{i18n.t("quickAnswersModal.mediaTypes.video")}</MenuItem>
                            <MenuItem value="file">{i18n.t("quickAnswersModal.mediaTypes.file")}</MenuItem>
                          </Select>
                        </FormControl>
                      </div>
                      <div className={classes.textQuickAnswerContainer}>
                        <input
                          accept="image/*,video/*,.pdf,.doc,.docx"
                          style={{ display: "none" }}
                          id="quick-answer-media-upload"
                          type="file"
                          onChange={handleMediaFileChange}
                          disabled={uploadingMedia}
                        />
                        <label htmlFor="quick-answer-media-upload">
                          <Button
                            variant="outlined"
                            component="span"
                            disabled={uploadingMedia}
                          >
                            {uploadingMedia
                              ? "..."
                              : values.mediaPath
                              ? i18n.t("quickAnswersModal.form.mediaFile") + " (" + values.mediaPath + ")"
                              : i18n.t("quickAnswersModal.form.mediaFile")}
                          </Button>
                        </label>
                        {errors.mediaPath && touched.mediaPath && (
                          <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                            {errors.mediaPath}
                          </div>
                        )}
                      </div>
                      {mediaPreviewUrl && (
                        <div>
                          <img
                            src={mediaPreviewUrl}
                            alt="Preview"
                            className={classes.mediaPreview}
                          />
                        </div>
                      )}
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.caption")}
                          name="caption"
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          multiline
                          rows={2}
                          fullWidth
                        />
                      </div>
                    </>
                  )}

                  {tabValue === 2 && (
                    <>
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.title")}
                          name="title"
                          error={touched.title && Boolean(errors.title)}
                          helperText={touched.title && errors.title}
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          fullWidth
                        />
                      </div>
                      <div className={classes.textQuickAnswerContainer}>
                        <TextField
                          label={i18n.t("quickAnswersModal.form.mapsUrl")}
                          name="mapsUrl"
                          value={values.mapsUrl || ""}
                          onChange={(e) => setFieldValue("mapsUrl", e.target.value)}
                          onBlur={handleMapsUrlBlur}
                          error={touched.mapsUrl && Boolean(errors.mapsUrl)}
                          helperText={
                            (touched.mapsUrl && errors.mapsUrl) ||
                            (values.latitude != null && values.longitude != null
                              ? `Lat: ${values.latitude}, Long: ${values.longitude}`
                              : "")
                          }
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          fullWidth
                          placeholder="https://www.google.com/maps?q=-33.8688,151.2093"
                        />
                      </div>
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.locationName")}
                          name="locationName"
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          fullWidth
                        />
                      </div>
                      <div className={classes.textQuickAnswerContainer}>
                        <Field
                          as={TextField}
                          label={i18n.t("quickAnswersModal.form.locationCaption")}
                          name="locationCaption"
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          multiline
                          rows={2}
                          fullWidth
                        />
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {quickAnswerId
                    ? i18n.t("quickAnswersModal.buttons.okEdit")
                    : i18n.t("quickAnswersModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickAnswersModal;
